/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import ButtonView from '@airasia-phoenix/react-native/components/ButtonView';
import IconView from '@airasia-phoenix/react-native/components/IconView';
import NotificationView, {
  NotificationViewStyle,
} from '@airasia-phoenix/react-native/components/NotificationView';
import TextFieldView from '@airasia-phoenix/react-native/components/TextFieldView';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import { pd } from '@airasia-phoenix/react-native/utils';
import { getColor } from '@airasia-phoenix/react-native/utils/utils';
import { useDeviceInfo } from '@airasia-rn/ui';
import { responsiveTextStyle } from '../../utils/responsiveTextStyle';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, TouchableOpacity, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useLogger } from '@airasia-rn/core-logger';
import { ACTIONS, CATEGORY, SCREEN_NAME } from '../../config/analytics';
import {
  createAnalyticsLogger,
  getSsoForgotPasswordLabels,
  logSsoError,
  SSO_ERROR_TYPE,
} from '../../utils/loginSignupAnalyticsUtils';
import { useRequestEmailOtp } from '../../hooks/otp/api';
import { useCultureCode } from '../../hooks/useCultureCode';
import { Purpose } from '../../managers/sso';
import { describeSsoError } from '../../utils/ssoErrors';
import { isValidEmail } from '../../utils/validations';
import { defaultProps, ForgotPasswordProps } from './types';

export default function ForgotPasswordView(props: ForgotPasswordProps) {
  const { t } = useTranslation('account');
  const {
    headings,
    emailFieldProps,
    primaryButtonProps,
    onLoadingChange,
    onBackPress,
    showHeader = true,
    onSendEmailSuccess,
  } = {
    ...defaultProps,
    ...props,
  };

  const { logEvent } = useLogger();
  const forgotPasswordLabels = getSsoForgotPasswordLabels();
  const logForgotPasswordAnalytics = useMemo(
    () =>
      createAnalyticsLogger(
        logEvent,
        SCREEN_NAME.forgotPasswordV2,
        CATEGORY.ssoLogin,
      ),
    [logEvent],
  );

  const logForgotPasswordError = useCallback(
    (
      type: string,
      code: string | number | undefined,
      description: string | undefined,
      message: string,
    ) =>
      logSsoError(logEvent, {
        screenName: SCREEN_NAME.forgotPasswordV2,
        type,
        code,
        description,
        message,
      }),
    [logEvent],
  );

  useEffect(() => {
    logForgotPasswordAnalytics(ACTIONS.screenView, forgotPasswordLabels.screen);
  }, [forgotPasswordLabels.screen, logForgotPasswordAnalytics]);

  const { isPending, mutate } = useRequestEmailOtp('ForgotPasswordView');
  const cultureCode = useCultureCode('ForgotPasswordView');

  const [email, setEmail] = useState(emailFieldProps.value ?? '');
  const [emailErrorMessage, setEmailErrorMessage] = useState('');
  useEffect(() => {
    onLoadingChange?.(isPending);
  }, [isPending, onLoadingChange]);

  // Notification state
  const [notification, setNotification] = useState<{
    text: string;
    style: NotificationViewStyle;
  } | null>(null);

  const handleForgotPasswordPress = useCallback(() => {
    logForgotPasswordAnalytics(ACTIONS.tap, forgotPasswordLabels.submit);

    const validationError = !email
      ? t('forgotPasswordPage.fieldRequired')
      : !isValidEmail(email)
        ? t('forgotPasswordPage.emailError')
        : '';

    if (validationError) {
      setEmailErrorMessage(validationError);
      logForgotPasswordError(
        SSO_ERROR_TYPE.validation,
        undefined,
        undefined,
        validationError,
      );
      return;
    }

    setEmailErrorMessage('');
    setNotification(null);

    mutate(
      { email, cultureCode, purpose: Purpose.FORGOT_PASSWORD },
      {
        onSuccess: () => onSendEmailSuccess?.(email),
        onError: (error: unknown) => {
          const { message, code, status, description } = describeSsoError(
            error,
            t,
            t('forgotPasswordPage.sendEmailError'),
          );
          logForgotPasswordError(
            SSO_ERROR_TYPE.api,
            status ?? code,
            description,
            message,
          );
          setNotification({
            text: message,
            style: NotificationViewStyle.ERROR,
          });
        },
      },
    );
  }, [
    cultureCode,
    email,
    forgotPasswordLabels.submit,
    logForgotPasswordAnalytics,
    logForgotPasswordError,
    mutate,
    onSendEmailSuccess,
    t,
  ]);

  const { isMobileBrowser } = useDeviceInfo();

  const handleEmailChange = useCallback(
    (text: string) => {
      setEmail(text);
      setNotification(null);
      if (!isValidEmail(text)) {
        setEmailErrorMessage(t('forgotPasswordPage.emailError'));
      } else {
        setEmailErrorMessage('');
      }
    },
    [t],
  );

  return (
    <View style={styles.mainContainer}>
      {/* Header Row - Web Only */}
      {showHeader && Platform.OS === 'web' && (
        <View style={styles.headerRow}>
          <TouchableOpacity onPress={onBackPress} testID="back-button">
            <IconView
              icon={
                isMobileBrowser
                  ? pd.icon.main.navigation.close
                  : pd.icon.main.navigation.arrow_back_platform
              }
              height={24}
              width={24}
              tint={getColor(pd.sys.colour.content_primary)}
              testID="back-icon"
            />
          </TouchableOpacity>
          <TextView
            {...headings.title}
            text={t('forgotPasswordPage.title')}
            style={[
              headings.title.style,
              {
                marginLeft: 12,
              },
            ]}
            testID="forgot-password-title"
          />
        </View>
      )}

      {notification && (
        <NotificationView
          style={notification.style}
          text={notification.text}
          width="100%"
        />
      )}
      {/* Greeting */}
      <TextView
        {...headings.greeting}
        style={styles.greeting}
        text={t('forgotPasswordPage.greeting')}
        testID="greeting-text"
      />

      {/* Email */}
      <TextFieldView
        {...emailFieldProps}
        label={t('forgotPasswordPage.emailLabel')}
        testID="email-input"
        value={email}
        isError={!!emailErrorMessage}
        errorMessage={emailErrorMessage}
        onChangeText={handleEmailChange}
      />

      {/* Primary Button */}
      <ButtonView
        {...primaryButtonProps}
        onPress={handleForgotPasswordPress}
        text={t('forgotPasswordPage.submitButton')}
        testID="submit-button"
      />
    </View>
  );
}

const styles = StyleSheet.create((pd) => ({
  mainContainer: {
    flex: 1,
    gap: pd.ref.space.spacing40.px,
    padding: pd.ref.space.spacing40.px,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: pd.ref.space.spacing20.px,
  },
  greeting: responsiveTextStyle(
    pd.sys.typescale.sub_body_text,
    pd.sys.typescale.body_text,
    pd.sys.colour.content_secondary,
  ),
}));
